import React, { useEffect, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import ChartOfAccountsMenu from "./components/ChartOfAccountsMenu.jsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Component imports
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Database from "./components/Database";
import ReminderDB from "./components/ReminderDB";
import CompanyInfoDB from "./components/CompanyInfoDB";
import CompanyProfileDB from "./components/CompanyProfileDB";
import EditCompanyProfile from "./components/EditCompanyProfile";
import CompanyInfo from "./components/CompanyInfo";
import TradeInfo from "./components/TradeInfo";
import ListingInfo from "./components/ListingInfo";
import LedgerDB from "./components/LedgerDB";
import MiscellaneousDB from "./components/MiscellaneousDB";
import TradeDB from "./components/TradeDB";
import MiscellaneousEntriesDB from "./components/MiscellaneousEntriesDB";
import MLSFeeDB from "./components/MLSFeeDB";
import EditLedger from "./components/EditLedger";
import EditMisc from "./components/EditMisc";
import ListingInfoDB from "./components/ListingInfoDB"; // Import the new component
import AgentInfo from "./components/AgentInfo";
import AgentInfoDB from "./components/AgentInfoDB";
import Finance from "./components/Finance";
import RealEstateTrustPayments from "./components/RealEstateTrustPayments";
import RealEstateTrustLedger from "./components/RealEstateTrustLedger";
import CommissionTrustPayments from "./components/CommissionTrustPayments";
import CommissionTrustLedger from "./components/CommissionTrustLedger";
import AgentExpenses from "./components/AgentExpenses";
import GeneralAccountPayments from "./components/GeneralAccountPayments";
import ReconcileRealEstateTrust from "./components/ReconcileRealEstateTrust";
import ReconcileCommissionTrust from "./components/ReconcileCommissionTrust";
import ReconcileGeneralAccount from "./components/ReconcileGeneralAccount";
import AccountsReceivable from "./components/AccountsReceivable";
import TrialBalance from "./components/TrialBalance";
import HSTReport from "./components/HSTReport";
import PeopleForm from "./components/PeopleForm";
import FinanceDB from "./components/FinanceDB";
import VendorInfoNew from "./components/VendorInfoNew";
import VendorInfoDB from "./components/VendorInfoDB";
import FinanceStatements from "./pages/FinanceStatements";
import AgentPaymentInfo from "./components/AgentPaymentInfo";
import JournalEntry from "./components/JournalEntry";
import DisbursementsJournals from "./components/DisbursementsJournals";
import TrustJournal from "./components/TrustJournal";
import TradeARJournal from "./components/TradeARJournal";
import FinancialReports from "./components/FinancialReports";
import FinancialReportsHST from "./components/FinancialReportsHST";
import FinancialReportsDisbursements from "./components/FinancialReportsDisbursements";
import FinancialReportsTrust from "./components/FinancialReportsTrust";
import FinancialReportsTradeAR from "./components/FinancialReportsTradeAR";
import FinancialReportsAgentSummary from "./components/FinancialReportsAgentSummary";
import FinancialReportsAgentPayment from "./components/FinancialReportsAgentsPayment";
import FinancialReportsActivityDetails from "./components/FinancialReportsActivityDetails";
import FinancialReportsOpenTradesCommission from "./components/FinancialReportsOpenTradesCommission";
import FinancialReportsCommission from "./components/FinancialReportsCommission";
import FinancialReportsPendingTrades from "./components/FinancialReportsPendingTrades";
import CustomReport from "./components/CustomReport";
import InvoiceDetailReport from "./components/InvoiceDetailReport";
import FinancePortal from "./components/FinancePortal";
import AgentPaymentSummary from "./components/AgentPaymentSummary";
import OutsideBrokersDB from "./components/OutsideBrokersDB";
import LawyersDB from "./components/LawyersDB";
import AccessLogPage from "./components/AccessLogPage";
import InvoiceSentTrackerPage from "./components/InvoiceSentTrackerPage";
import TrustMailTrackerPage from "./components/TrustMailTrackerPage";
import TradeDashboard from "./components/TradeDashboard";
import EFTReversalPage from "./components/EFTReversalPage";
import AgentTradeStatus from "./components/AgentTradeStatus";
import OfficeDeductionsReport from "./components/OfficeDeductionsReport";
import SuperUserDraftReview from "./components/SuperUserDraftReview";
import OfficeInbox from "./components/OfficeInbox";
import UserAccess from "./components/UserAccess";
import PayrollEftReports from "./pages/PayrollEftReports";
import T4APage from "./pages/T4APage";
import T4ASummaryPage from "./pages/T4ASummaryPage";
import axiosInstance from "./config/axios";
import { RoleProvider } from "./context/RoleContext";
import {
  clearAppSession,
  getAppRole,
  getAppSession,
  getFirebaseLoginAt,
  setAppSession,
  setFirebaseLoginAt,
} from "./utils/appAuth";
import useAbsoluteSessionTimeout from "./hooks/useAbsoluteSessionTimeout";
import { isFinanceAllowedPath } from "./constants/financePortalLinks";

function safeNextPath(value) {
  const next = String(value || "");
  if (next.startsWith("/") && !next.startsWith("//")) return next;
  return "";
}

function HomeRoute({ isAuthenticated, setUser, setAppSessionState }) {
  const [params] = useSearchParams();
  const next = safeNextPath(params.get("next"));
  if (isAuthenticated) {
    const session = getAppSession();
    const role = getAppRole(null, session);
    if (next) {
      if (role === "finance" && !isFinanceAllowedPath(next.split("?")[0])) {
        return <Navigate to="/finance-portal" replace />;
      }
      return <Navigate to={next} replace />;
    }
    if (role === "finance") return <Navigate to="/finance-portal" replace />;
    return <Dashboard />;
  }
  return <Login setUser={setUser} setAppSessionState={setAppSessionState} />;
}

function RequireAuth({ ok, isAuthenticated, children }) {
  const location = useLocation();
  if (ok) return children;
  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [appSession, setAppSessionState] = useState(getAppSession());
  const [authChecked, setAuthChecked] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(!getAppSession()?.token);
  const lastLoggedUidRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const stored = getAppSession();
      if (
        firebaseUser &&
        stored?.token &&
        stored?.user?.role &&
        stored.user.role !== "admin"
      ) {
        signOut(auth).catch(() => {});
        setUser(null);
        setAppSessionState(stored);
        setAuthChecked(true);
        return;
      }
      setUser(firebaseUser);
      if (firebaseUser) {
        clearAppSession();
        setAppSessionState(null);
        if (!getFirebaseLoginAt()) {
          setFirebaseLoginAt(Date.now());
        }
      }
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const stored = getAppSession();
    if (!stored?.token) {
      setSessionChecked(true);
      return;
    }
    axiosInstance
      .get("/auth/me")
      .then((res) => {
        const next = {
          token: stored.token,
          user: res.data.user,
          loginAt: stored.loginAt || Date.now(),
        };
        setAppSession(next);
        setAppSessionState(next);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          clearAppSession();
          setAppSessionState(null);
        }
      })
      .finally(() => setSessionChecked(true));
  }, []);

  // Record access when user signs in (once per session per user)
  useEffect(() => {
    const email = String(
      user?.email || appSession?.user?.email || "",
    ).trim();
    const name = String(
      user?.displayName ||
        appSession?.user?.displayName ||
        email ||
        "",
    ).trim();
    const sessionKey =
      user?.uid ||
      appSession?.user?._id ||
      (email ? `email:${email.toLowerCase()}` : "");
    if (!sessionKey || !email || sessionKey === lastLoggedUidRef.current) {
      return;
    }
    lastLoggedUidRef.current = sessionKey;

    const recordAccess = async () => {
      try {
        const res = await axiosInstance.post("/access-log", {
          userEmail: email,
          userName: name,
        });
        if (res?.data?._id) {
          sessionStorage.setItem("accessLogEntryId", res.data._id);
        }
      } catch (_) {}
    };

    recordAccess();
  }, [user, appSession]);

  const isAuthenticated = Boolean(user || appSession?.token);
  const role = getAppRole(user, appSession) || (user ? "admin" : null);

  useAbsoluteSessionTimeout({
    role,
    isAuthenticated: authChecked && sessionChecked && isAuthenticated,
    firebaseUser: user,
    appSession,
    setUser,
    setAppSessionState,
  });

  if (!authChecked || !sessionChecked) {
    return <div>Loading...</div>; // Prevents premature render
  }

  const gate = (allow) => {
    if (!isAuthenticated) return false;
    if (!allow) return true;
    return allow.includes(role);
  };

  return (
    <>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <RoleProvider firebaseUser={user} appSession={appSession}>
        <div className="app-canvas">
        <Toaster position="top-center" reverseOrder={false} />
        <Routes>
          <Route
            path="/"
            element={
              <HomeRoute
                isAuthenticated={isAuthenticated}
                setUser={setUser}
                setAppSessionState={setAppSessionState}
              />
            }
          />
          <Route
            path="/database"
            element={gate(["admin", "superuser"]) ? <Database /> : <Navigate to="/" replace />}
          />
          <Route
            path="/database/reminders"
            element={gate(["admin", "superuser"]) ? <ReminderDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/database/company-info-db"
            element={gate(["admin"]) ? <CompanyInfoDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/company-profile-db"
            element={gate(["admin"]) ? <CompanyProfileDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/edit-company-profile"
            element={
              gate(["admin"]) ? <EditCompanyProfile /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/company-info"
            element={gate(["admin"]) ? <CompanyInfo /> : <Navigate to="/" replace />}
          />
          <Route
            path="/trade-info"
            element={
              <RequireAuth
                ok={gate(["admin", "superuser", "agent", "broker"])}
                isAuthenticated={isAuthenticated}
              >
                <TradeInfo user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/listing-info"
            element={
              gate(["admin", "superuser", "agent", "broker"]) ? (
                <ListingInfo />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/ledger-db"
            element={gate(["admin", "superuser"]) ? <LedgerDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/miscellaneous-db"
            element={gate(["admin", "superuser"]) ? <MiscellaneousDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/trade-db"
            element={gate(["admin", "superuser"]) ? <TradeDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/miscellaneous-entries-db"
            element={
              gate(["admin", "superuser"]) ? <MiscellaneousEntriesDB /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/mls-fee-db"
            element={gate(["admin", "superuser"]) ? <MLSFeeDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/edit-ledger/:id"
            element={gate(["admin", "superuser"]) ? <EditLedger /> : <Navigate to="/" replace />}
          />
          <Route
            path="/edit-misc/:id"
            element={gate(["admin", "superuser"]) ? <EditMisc /> : <Navigate to="/" replace />}
          />
          <Route
            path="/listing-info-db"
            element={gate(["admin", "superuser", "broker"]) ? <ListingInfoDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/agent-info"
            element={gate(["admin", "superuser", "broker"]) ? <AgentInfo /> : <Navigate to="/" replace />}
          />
          <Route
            path="/agent-info-db"
            element={gate(["admin", "superuser", "broker"]) ? <AgentInfoDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/finance"
            element={gate(["admin", "superuser"]) ? <Finance /> : <Navigate to="/" replace />}
          />
          <Route
            path="/real-estate-trust-payments"
            element={
              gate(["admin", "superuser"]) ? <RealEstateTrustPayments /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/real-estate-trust-ledger"
            element={
              gate(["admin", "superuser"]) ? <RealEstateTrustLedger /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/commission-trust-payments"
            element={
              gate(["admin", "superuser"]) ? <CommissionTrustPayments /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/commission-trust-ledger"
            element={
              gate(["admin", "superuser"]) ? <CommissionTrustLedger /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/agent-expenses"
            element={gate(["admin", "superuser"]) ? <AgentExpenses /> : <Navigate to="/" replace />}
          />
          <Route
            path="/general-account-payments"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <GeneralAccountPayments />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/chart-of-accounts"
            element={
              gate(["admin", "superuser"]) ? <ChartOfAccountsMenu /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/trial-balance"
            element={gate(["admin", "superuser"]) ? <TrialBalance /> : <Navigate to="/" replace />}
          />
          <Route
            path="/hst-report"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <HSTReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/people-form"
            element={gate(["admin", "superuser"]) ? <PeopleForm /> : <Navigate to="/" replace />}
          />
          <Route
            path="/finance-db"
            element={gate(["admin", "superuser"]) ? <FinanceDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/vendor-info"
            element={gate(["admin", "superuser", "broker"]) ? <VendorInfoNew /> : <Navigate to="/" replace />}
          />
          <Route
            path="/vendor-info-db"
            element={gate(["admin", "superuser", "broker"]) ? <VendorInfoDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/finance-statements"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <FinanceStatements />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/reconcile-real-estate-trust"
            element={
              gate(["admin", "superuser"]) ? <ReconcileRealEstateTrust /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/reconcile-commission-trust"
            element={
              gate(["admin", "superuser"]) ? <ReconcileCommissionTrust /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/reconcile-general-account"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <ReconcileGeneralAccount />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/eft-reversal"
            element={gate(["admin", "superuser"]) ? <EFTReversalPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/accounts-receivable"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <AccountsReceivable />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/agent-payment-info"
            element={gate(["admin", "superuser"]) ? <AgentPaymentInfo /> : <Navigate to="/" replace />}
          />
          <Route
            path="/agent-payment-summary"
            element={
              gate(["admin", "superuser"]) ? <AgentPaymentSummary /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/journal-entry"
            element={
              gate(["admin", "superuser", "finance"]) ? (
                <JournalEntry />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-portal"
            element={
              gate(["finance"]) ? (
                <FinancePortal />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/disbursements-journals"
            element={
              gate(["admin", "superuser"]) ? <DisbursementsJournals /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/trust-journal"
            element={gate(["admin", "superuser"]) ? <TrustJournal /> : <Navigate to="/" replace />}
          />
          <Route
            path="/trade-ar-journal"
            element={gate(["admin", "superuser"]) ? <TradeARJournal /> : <Navigate to="/" replace />}
          />
          <Route
            path="/financial-reports"
            element={gate(["admin", "superuser", "broker"]) ? <FinancialReports /> : <Navigate to="/" replace />}
          />
          <Route
            path="/trade-dashboard"
            element={gate(["admin", "superuser", "broker"]) ? <TradeDashboard /> : <Navigate to="/" replace />}
          />
          <Route
            path="/financial-reports-hst"
            element={
              gate(["admin", "superuser", "agent", "broker"]) ? (
                <FinancialReportsHST />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-disbursements"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsDisbursements />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-trust"
            element={
              gate(["admin", "superuser", "broker"]) ? <FinancialReportsTrust /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/financial-reports-trade-ar"
            element={
              gate(["admin", "superuser", "broker"]) ? <FinancialReportsTradeAR /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/financial-reports-agent-payment"
            element={
              gate(["admin", "superuser", "agent", "broker"]) ? (
                <FinancialReportsAgentPayment />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-agent-payment-summary"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsAgentSummary />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-activity-details"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsActivityDetails />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-activity-details"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsActivityDetails />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-open-trades-commission"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsOpenTradesCommission />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-open-trades-commission"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsOpenTradesCommission />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-commission"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsCommission />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-commission-report"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsCommission />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-pending-trades"
            element={
              gate(["admin", "superuser", "agent", "broker"]) ? (
                <FinancialReportsPendingTrades />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-pending-trade-report"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <FinancialReportsPendingTrades />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-custom"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <CustomReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/financial-reports-invoice-detail"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <InvoiceDetailReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-custom-report"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <CustomReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/finance-invoice-detail-report"
            element={
              gate(["admin", "superuser", "broker"]) ? (
                <InvoiceDetailReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/outside-brokers-db"
            element={gate(["admin", "superuser"]) ? <OutsideBrokersDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/lawyers-db"
            element={gate(["admin", "superuser"]) ? <LawyersDB /> : <Navigate to="/" replace />}
          />
          <Route
            path="/access-log"
            element={gate(["admin", "superuser", "broker"]) ? <AccessLogPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/invoice-sent"
            element={
              gate(["admin", "superuser"]) ? (
                <InvoiceSentTrackerPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/trust-mail-sent"
            element={
              gate(["admin", "superuser"]) ? (
                <TrustMailTrackerPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/user-access"
            element={gate(["admin"]) ? <UserAccess /> : <Navigate to="/" replace />}
          />
          <Route
            path="/inbox"
            element={
              gate(["superuser", "admin"]) ? (
                <OfficeInbox />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/draft-review"
            element={
              gate(["superuser", "admin"]) ? (
                <SuperUserDraftReview />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/agent-trade-status"
            element={
              gate(["agent", "superuser", "admin"]) ? (
                <AgentTradeStatus />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payroll/eft-reports"
            element={
              gate(["agent", "admin", "superuser"]) ? (
                <PayrollEftReports />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payroll/t4a"
            element={
              gate(["agent", "admin", "superuser"]) ? (
                <T4APage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payroll/t4a-summary"
            element={
              gate(["admin", "superuser"]) ? (
                <T4ASummaryPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/accounting/office-deductions"
            element={
              gate(["admin", "superuser", "agent", "broker"]) ? (
                <OfficeDeductionsReport />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
        </div>
        </RoleProvider>
      </Router>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;